/**
 * Workspace · My Office (Wave E).
 * Aggregates Today + Calendar + Approvals + Meetings + Files + Notes
 * + Notifications + My KPI + My Tasks into one cockpit view.
 * Reuses only existing primitives.
 */
import { Sparkles, Calendar, FileText, Bell, Target, ListChecks } from "lucide-react";
import { Card, SectionHeader } from "../../components/lib";
import { ListRow } from "../../components/lib.overlay";
import { TASKS, WS_FILES } from "../../mocks/workspaceData";
import { AI_INSIGHTS, AI_APPROVALS } from "../../mocks/aiData";
import { MEETINGS } from "../../mocks/meetingsData";
import { OKRS } from "../../mocks/okrsData";

export default function MyDay() {
  const today = TASKS.filter((t) => t.dueRelative === "due today" || t.dueRelative === "due tomorrow" || t.dueRelative.startsWith("overdue"));
  const insights = AI_INSIGHTS.filter((i) => !i.acted).slice(0, 3);
  const approvals = AI_APPROVALS.filter((a) => a.status === "pending");
  const upcomingMeetings = MEETINGS.filter((m) => m.status === "scheduled").slice(0, 4);
  const recentFiles = WS_FILES.slice(0, 4);
  const myOkrs = OKRS.slice(0, 3);

  return (
    <div className="space-y-6" data-testid="ws-myday">
      {/* ── Hero: Today ─────────────────────────────────────────── */}
      <Card testId="ws-md-hero">
        <SectionHeader eyebrow="My Office · Today" title="Your cockpit at a glance" action={<Sparkles size={14} style={{ color: "var(--ph-ai)" }}/>} />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Due today</div><div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>{today.length}</div></div>
          <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Approvals waiting</div><div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-warning)" }}>{approvals.length}</div></div>
          <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Meetings ahead</div><div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-brand-primary)" }}>{upcomingMeetings.length}</div></div>
          <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Open insights</div><div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-danger)" }}>{insights.length}</div></div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* My Tasks */}
        <Card padded={false} testId="ws-md-tasks">
          <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
            <SectionHeader eyebrow="Priorities" title="My tasks" action={<ListChecks size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
          </div>
          <div className="p-2">
            {today.length === 0 && <div className="p-4 text-sm" style={{ color: "var(--ph-ink-muted)" }}>Nothing on fire. Good time for deep work.</div>}
            {today.map((t) => (
              <ListRow key={t.id} testId={`ws-md-t-${t.id}`}
                label={t.title}
                sublabel={<>{t.module} · {t.owner} · {t.dueRelative}</>}
                trailing={<span className="text-[10px] uppercase tracking-[0.14em]" style={{ color: t.priority === "high" ? "var(--ph-danger)" : t.priority === "medium" ? "var(--ph-warning)" : "var(--ph-ink-muted)" }}>{t.priority}</span>}
              />
            ))}
          </div>
        </Card>

        {/* Calendar / Meetings */}
        <Card padded={false} testId="ws-md-calendar">
          <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
            <SectionHeader eyebrow="Calendar" title="Upcoming meetings" action={<Calendar size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
          </div>
          <div className="p-2">
            {upcomingMeetings.length === 0 && <div className="p-4 text-sm" style={{ color: "var(--ph-ink-muted)" }}>Calendar is clear.</div>}
            {upcomingMeetings.map((m) => (
              <ListRow key={m.id} testId={`ws-md-m-${m.id}`}
                label={m.title}
                sublabel={<>{m.kind} · {m.scheduledRelative} · {(m.attendees || []).length} attendees</>}
              />
            ))}
          </div>
        </Card>

        {/* Approvals */}
        <Card padded={false} testId="ws-md-approvals">
          <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
            <SectionHeader eyebrow="Approvals" title="Waiting for you" />
          </div>
          <div className="p-2">
            {approvals.length === 0 && <div className="p-4 text-sm" style={{ color: "var(--ph-success)" }}>Inbox zero.</div>}
            {approvals.map((a) => (
              <ListRow key={a.id} testId={`ws-md-a-${a.id}`}
                label={a.title}
                sublabel={<>{a.askedBy} · {a.askedRelative}</>}
                trailing={<span className="text-sm tabular-nums">EGP {a.amountEGP.toLocaleString()}</span>}
              />
            ))}
          </div>
        </Card>

        {/* Files */}
        <Card padded={false} testId="ws-md-files">
          <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
            <SectionHeader eyebrow="Files" title="Recently touched" action={<FileText size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
          </div>
          <div className="p-2">
            {recentFiles.map((f) => (
              <ListRow key={f.id} testId={`ws-md-f-${f.id}`}
                label={f.name}
                sublabel={<>{f.kind} · {f.updatedRelative} · {f.owner}</>}
              />
            ))}
          </div>
        </Card>

        {/* My KPI (OKRs) */}
        <Card padded={false} testId="ws-md-kpis">
          <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
            <SectionHeader eyebrow="My KPI" title="OKR progress" action={<Target size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
          </div>
          <div className="p-2">
            {myOkrs.map((o) => (
              <ListRow key={o.id} testId={`ws-md-okr-${o.id}`}
                label={o.objective}
                sublabel={<>{o.owner} · {o.cycle}</>}
                trailing={<span className="text-sm tabular-nums font-semibold" style={{ color: o.progressPct >= 70 ? "var(--ph-success)" : o.progressPct >= 40 ? "var(--ph-warning)" : "var(--ph-danger)" }}>{o.progressPct}%</span>}
              />
            ))}
          </div>
        </Card>

        {/* Notifications / AI Insights */}
        <Card padded={false} testId="ws-md-insights">
          <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
            <SectionHeader eyebrow="AI · Notifications" title="Insights + notes" action={<Bell size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
          </div>
          <div className="p-2">
            {insights.map((i) => (
              <ListRow key={i.id} testId={`ws-md-i-${i.id}`} label={i.title} sublabel={i.detail} trailing={<span className="text-[10px]" style={{ color: "var(--ph-ai)" }}>{i.severity}</span>} />
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
